<script setup>
import { computed, onMounted, onUnmounted, ref } from "vue";
import { Actions, ChangeSectionPayload, EventManager, Events } from "@events";
import VideoCallButton from "@/components/Common/VideoCallButton.vue";
import { PhotoPanelActions, PhotoPanelEvents } from "../helpers/photoPanelActions";
import { useVideoCallState } from "@/stores/videoCallState";

const props = defineProps({
  rtcId: {
    type: String,
    required: true,
  },
});

// selected document type and face
const selectedDocumentType = ref(null);
const selectedDocumentFace = ref(null);
const actionEmitter = EventManager.getActionEmitter(props.rtcId);
const eventEmitter = EventManager.getEventEmitter(props.rtcId);
const isPanelOpen = ref(false);
const state = useVideoCallState(props.rtcId);

actionEmitter.on(PhotoPanelActions.OpenTypeSelectionPanel, () => {
  isPanelOpen.value = true;
  // if there is only one document type left not sent, select it
  const notSentDocumentTypes = documentTypes.value.filter((x) => !x.isSent);
  if (notSentDocumentTypes.length == 1) {
    selectedDocumentType.value = notSentDocumentTypes[0];
    docTypeChanged();
  }
});

actionEmitter.on(PhotoPanelActions.ClosePhotoPanel, () => {
  closePanel();
});

actionEmitter.on(PhotoPanelActions.ClearDocFaceSelection, () => {
  docTypeChanged();
});

function triggerClosePanel() {
  actionEmitter.emit(PhotoPanelActions.ClosePhotoPanel);
}

function closePanel() {
  isPanelOpen.value = false;
  selectedDocumentType.value = null;
  selectedDocumentFace.value = null;
  takePhotoOff();
}

eventEmitter.on(Events.PhotoDeleted, ({ documentTypeId, documentTypeFaceId }) => {
  // set isSent property of document type face
  const documentTypeFace = state.callData.queue.documentTypeFaces.find(
    (x) => x.documentType.id == documentTypeId && x.id == documentTypeFaceId,
  );

  if (documentTypeFace) {
    documentTypeFace.isSent = false;
  }
});

eventEmitter.on(PhotoPanelEvents.PhotoUploaded, ({ documentTypeId, documentTypeFaceId }) => {
  let face = state.callData.queue.documentTypeFaces.find(
    (x) => x.documentType.id == documentTypeId && x.id == documentTypeFaceId,
  );

  let documentType = documentTypes.value.find((x) => x.id == documentTypeId);

  face.isSent = true;

  // if all faces are sent, set document type as sent
  var notSendFaces = documentType.faces.filter((x) => !x.isSent);

  if (notSendFaces.length == 0) {
    documentType.isSent = true;
  }
});

// #region ###############   documentType and document face computed values for first step select elements  ###############

const documentTypes = computed(() => {
  const documentTypeFaces = state.callData?.queue?.documentTypeFaces ?? [];

  // group document types by documentType.id and documentTypeName of documentTypeFaces

  const documentTypeGroups = documentTypeFaces.reduce((acc, curr) => {
    const existing = acc.find((x) => x.id == curr.documentType.id);
    if (existing) {
      existing.faces.push(curr);
    } else {
      acc.push({
        id: curr.documentType.id,
        name: curr.documentTypeName,
        order: curr.documentType.order,
        faces: [curr],
      });
    }
    return acc;
  }, []);

  // set isSent property of document types if all faces are sent
  documentTypeGroups.forEach((x) => {
    x.isSent = x.faces.every((y) => y.isSent);
  });

  // order by "order" property
  documentTypeGroups.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  // order each document type faces by "order" property
  documentTypeGroups.forEach((x) => {
    x.faces.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  });

  return documentTypeGroups;
});

function docTypeChanged() {
  selectedDocumentFace.value = null;
  if (selectedDocumentType.value == null) return;

  var notSendedFaces = selectedDocumentType.value.faces.filter((x) => !x.isSent);

  if (notSendedFaces.length == 0) {
    var notSendedDocTypes = documentTypes.value.filter((x) => !x.isSent);
    if (notSendedDocTypes.length > 0) {
      selectedDocumentType.value = notSendedDocTypes[0];
      notSendedFaces = selectedDocumentType.value.faces.filter((x) => !x.isSent);
    } else {
      selectedDocumentType.value = null;
      actionEmitter.emit(PhotoPanelActions.ClosePhotoPanel);
      return;
    }
  }

  selectedDocumentFace.value = notSendedFaces[0];
}

const selectionOk = computed(() => {
  return selectedDocumentType.value != null && selectedDocumentFace.value != null;
});

const takePhotoButtonClass = computed(() => {
  return "ecv-call-button take-photo-button " + (selectionOk.value ? "" : "disabled");
});

function chooseDocument() {
  console.log("chooseDocument selectedDocumentFace.documentType.id", selectedDocumentFace.value.documentType.id);
  console.log("chooseDocument selectedDocumentFace.Id", selectedDocumentFace.value.id);
  console.log("chooseDocument selectedDocumentFace.cameraType", selectedDocumentFace.value.cameraType);

  takePhotoOn();
}

function takePhotoOn() {
  const remotePeer = state.peers.find((x) => x.type == "remote");

  if (!remotePeer) {
    console.error("takePhotoOn Remote peer not found. Cannot switch camera");
    triggerClosePanel();
    return;
  }

  eventEmitter.emit(PhotoPanelEvents.PhotoPanelTakePhotoOn, {
    documentTypeFace: selectedDocumentFace.value,
  });

  const documentCamera = selectedDocumentFace.value.cameraType == 0 ? "f" : "b";
  console.log("takePhotoOn documentCamera", documentCamera);

  const currentRemoteCamera = remotePeer.camera;
  console.log("takePhotoOn currentRemoteCamera", currentRemoteCamera);
  var swapping = documentCamera != currentRemoteCamera;
  if (swapping && currentRemoteCamera == "b" && documentCamera == "f") {
    // due to old sdk not supporting TAKEPHOTO_ON with back camera directly, we need to TAKEPHOTO_OFF|SWAP first to switch to front camera
    sendCameraMessage(false, true);
    // wait for CAMERASTATUS|f from remote side
    const cameraStatusHandler = ({ data }) => {
      if (data == "CAMERASTATUS|f") {
        eventEmitter.off(Events.TextChannelMessageReceived, cameraStatusHandler);
        // now we can send TAKEPHOTO_ON|NOSWAP message
        sendCameraMessage(true, false, selectedDocumentFace.value.documentType.id + selectedDocumentFace.value.id);
      }
    };
    eventEmitter.on(Events.TextChannelMessageReceived, cameraStatusHandler);
  } else {
    // normal case - just send TAKEPHOTO_ON message
    sendCameraMessage(true, swapping, selectedDocumentFace.value.documentType.id + selectedDocumentFace.value.id);
  }

  isPanelOpen.value = false;
}

function takePhotoOff() {
  const remotePeer = state.peers.find((x) => x.type == "remote");

  if (!remotePeer) {
    console.error("takePhotoOff Remote peer not found. Cannot switch camera");
    triggerClosePanel();
    return;
  }

  eventEmitter.emit(PhotoPanelEvents.PhotoPanelTakePhotoOff);

  const currentRemoteCamera = remotePeer.camera;
  console.log("takePhotoOff currentRemoteCamera", currentRemoteCamera);

  var swapping = currentRemoteCamera != "f";
  sendCameraMessage(false, swapping);
}

function sendCameraMessage(takePhoto = false, swap = false, message = null) {
  var messageParts = [];
  if (takePhoto) messageParts.push("TAKEPHOTO_ON");
  else messageParts.push("TAKEPHOTO_OFF");

  if (swap) messageParts.push("SWAP");
  else messageParts.push("NOSWAP");

  if (message) messageParts.push(message);

  actionEmitter.emit(Actions.SendTextChannelMessage, {
    message: messageParts.join("|"),
  });
}
// #endregion
</script>

<template>
  <div v-if="isPanelOpen" class="document-type-selection-panel">
    <v-row>
      <v-col cols="5">
        <v-input class="hideDetails">
          <v-field :label="selectedDocumentType ? '' : $t('SelectDocumentType')">
            <select
              no-details
              style="padding: 10px; color: white; width: 100%"
              v-model="selectedDocumentType"
              @change="docTypeChanged">
              <option
                style="color: black"
                v-for="item in documentTypes"
                :key="item.id"
                :value="item"
                :disabled="item.isSent"
                :class="item.isSent ? 'disabled' : ''">
                {{ item.name }}
              </option>
            </select>
          </v-field>
        </v-input>
      </v-col>
      <v-col cols="4">
        <v-input class="hideDetails">
          <v-field :label="selectedDocumentFace ? '' : $t('SelectDocumentFace')">
            <select style="padding: 10px; color: white; width: 100%" v-model="selectedDocumentFace">
              <option
                style="color: black"
                v-for="item in selectedDocumentType?.faces ?? []"
                :key="item.id"
                :value="item"
                :disabled="item.isSent"
                :class="item.isSent ? 'disabled' : ''">
                {{ item.name }}
              </option>
            </select>
          </v-field>
        </v-input>
      </v-col>
      <v-col :cols="3">
        <v-row>
          <v-col>
            <VideoCallButton
              icon="mdi-check"
              :class="takePhotoButtonClass"
              :disabled="!selectionOk"
              @click="chooseDocument"></VideoCallButton>
          </v-col>
          <v-col>
            <VideoCallButton
              style="background-color: rgb(221, 44, 0); border-color: rgb(221, 44, 0)"
              icon="mdi-close"
              @click="triggerClosePanel"></VideoCallButton>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </div>
</template>

<style scoped lang="scss">
.document-type-selection-panel {
  padding: 10px;
  background-color: #222222c2;
  border-radius: 16px;
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  width: 800px;
  z-index: 200;
}
</style>

<style lang="scss">
.hideDetails .v-input__details {
  display: none !important;
}
</style>
